import React, {Component} from 'react';
import PropTypes from 'prop-types';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import appConfig from 'config/app.config';
import apiHelper from 'helpers/api-helper';
import {getCookie, setCookie, deleteCookie} from 'helpers/cookie-helper';
import Survey from 'components/survey/survey';
import PopupConfirmCookies from 'components/ui/popup-confirm-cookies/popup-confirm-cookies';
import FacilitatorController from 'components/users/facilitator-controller';
import GroupController from 'components/users/group-controller';
import Login from './login';
import Button from 'components/ui/button/button';
import './login.scss';

class LoginController extends Component {
	constructor(props) {
		super(props);
		this.state = {
			isLoading: true,
			isLoggedIn: false,
			cookiesAccepted: false,
			showCookiePopup: false,
			userId: null,
			isFacilitator: false,
			isCreatingUser: false,
			isResettingPassword: false,
		};
		this.unsubscribeOnAuthStateChanged = null;
	}

	/**
	 * Component did mount
	 */
	componentDidMount = () => {

		const isEvaluation = window.location.pathname.toLowerCase().includes('survey');

		if (!isEvaluation) {
		/* Check cookie consent */
			const cookieConsentData = getCookie(appConfig.cookieConsentCookieName);
			if (cookieConsentData && cookieConsentData.length > 0) {
				this.setState({cookiesAccepted: true});
			} else {
				this.setState({showCookiePopup: true});
			}

			/* Check if logged in */
			this.checkIfLoggedIn();
		}
	};

	/**
	 * Component will unmount
	 */
	componentWillUnmount = () => {
		if (this.unsubscribeOnAuthStateChanged !== null) this.unsubscribeOnAuthStateChanged();
	};

	/**
	 * Check if user is logged in
	 */
	checkIfLoggedIn = () => {
		// Unsubscribe previous onAuthStateChanged
		if (this.unsubscribeOnAuthStateChanged !== null) {
			this.unsubscribeOnAuthStateChanged();
		}

		// Subscribe to onAuthStateChanged
		this.unsubscribeOnAuthStateChanged = firebase.auth().onAuthStateChanged((user)=>{
			if (user) {
				/* Ok to cookies */
				const cookieConsent = getCookie(appConfig.cookieConsentCookieName);
				if (!cookieConsent) {
					setCookie(appConfig.cookieConsentCookieName, 'ok');
				}

				const isFacilitator = Boolean(user.email !== null);
				if (isFacilitator) {
					/* Logged in as facilitator */
					this.setState({
						isLoggedIn: true,
						isLoading: false,
						userId: user.uid,
						isFacilitator: isFacilitator,
						email: user.email
					});
				} else {
					/* Logged in as group */
					this.setState({isLoggedIn: true, isLoading: false, userId: user.uid});
				}
			} else {
				this.setState({
					isLoading: false,
					isLoggedIn: false,
					userId: null,
					isFacilitator: false,
					isAdmin: false,
					email: null,
				});
			}
		});	
	};

	/**
	 * Toggle cookie popup
	 * @param {bool} showCookiePopup 
	 */
	toggleCookiePopup = (showCookiePopup) => {
		this.setState({showCookiePopup});
	};

	/**
	 * Accept / deny cookies
	 * @param {bool} isAccepted 
	 */
	toggleAcceptCookies = (isAccepted) => {
		if (isAccepted) {
			/* Cookies accepted, save choice in cookie */
			setCookie(appConfig.cookieConsentCookieName, 'ok');
			this.setState({cookiesAccepted: true});
		} else {
			/* Cookies denied, delete cookies (if any) and log out */
			deleteCookie(appConfig.cookieConsentCookieName);
			this.setState({cookiesAccepted: false}, () => {
				if (this.state.isLoggedIn) {
					this.handleLogout();
				}
			});
		}

		/* Close cookie popup */
		this.setState({showCookiePopup: false});
	};

	/**
	 * Facilitator login: Log in with email and password
	 * @param {string} email 
	 * @param {string} password 
	 */
	facilitatorLogin = (email, password) => {
		return firebase.auth().signInWithEmailAndPassword(email, password);
	};

	/**
	 * Group login: Create new group and get login token
	 * @param {string} gameId 
	 * @param {string} themeId
	 * @param {string} groupName 
	 * @returns 
	 */
	createGroup = (gameId, themeId, groupName) => {
		return new Promise((resolve) => {
			apiHelper('group/create-group', {gameId, themeId, groupName}).then((response)=>{
				if (response.status === 'success' && response.token) {
					resolve({status: 'success', token: response.token});
				} else {
					resolve({status: 'error', error: response.error});
					// if (response.error === 'group-name-already-exists') {
				}
			}).catch((error) => {
				resolve({status: 'error', error});
			});
		});
	};

	/**
	 * Group login: Get a login token for selected group
	 * @param {number} gameId 
	 * @param {number} groupId 
	 */
	getLoginToken = (gameId, groupId) => {
		return new Promise((resolve) => {
			apiHelper('group/join-game', {gameId, groupId}).then((response)=>{
				if (response.status === 'success' && response.token) {
					resolve({status: 'success', token: response.token});
				} else {
					resolve({status: 'error', error: response.error});
				}
			}).catch((error) => {
				resolve({status: 'error', error});
			});
		});
	};

	/**
	 * Group login: Login with token
	 * @param {string} token 
	 */
	loginWithToken = (token) => {
		return firebase.auth().signInWithCustomToken(token);
	};

	/**
	 * Logout
	 */
	handleLogout = () => {
		firebase.auth().signOut();
		this.setState({
			isLoggedIn: false,
			userId: null,
			isFacilitator: false,
			email: null,
		});
	};

	/**
	 * Set create user state
	 * @param {bool} isCreating 
	 */
	setCreateUser = (isCreating) => {
		this.setState({isCreatingUser: isCreating});
	};

	/**
	 * Set password reset state
	 * @param {bool} isResetting 
	 */
	setPasswordReset = (isResetting) => {
		this.setState({isResettingPassword: isResetting});
	};

	/**
 	* Render component
 	*/
	render = () => {	
		/* Survey */
		if (window.location.pathname.toLowerCase().includes('survey')) {
			return (
				<Survey 
					languageId={this.props.languageId}
					deviceInfo={this.props.deviceInfo}
				/>
			);
		}

		/* User is not loggged in */
		if (!this.state.isLoggedIn) {
			const loginRole = (window.location.pathname.toLowerCase().includes('gruppe') ? 'player' : 'facilitator');
			return (
				<>
					<Login 
						cookiesAccepted={this.state.cookiesAccepted}
						languageId={this.props.languageId}
						loginRole={loginRole}
						deviceInfo={this.props.deviceInfo}
						toggleCookiePopup={this.toggleCookiePopup}
						facilitatorLogin={this.facilitatorLogin}
						createGroup={this.createGroup}
						getLoginToken={this.getLoginToken}
						loginWithToken={this.loginWithToken}
						setLanguageId={this.props.setLanguageId}
						calculateFontSize={this.props.calculateFontSize}
					/>
					<Button 
						classes={['cookie', this.props.deviceInfo.orientation, loginRole]} 
						onClick={() => {this.toggleCookiePopup(true);}} 
					/>
					{this.state.showCookiePopup && 
						<PopupConfirmCookies 
							languageId={this.props.languageId}
							loginRole={loginRole}
							deviceInfo={this.props.deviceInfo}
							toggleAcceptCookies={this.toggleAcceptCookies}
							toggleCookiePopup={this.toggleCookiePopup}
						/>
					}
				</>
			);
		}

		

		/* User is logged in, get controller component for facilitator / group */
		let UserComponent = (this.state.isFacilitator ? FacilitatorController : GroupController);
		return (
			<UserComponent 
				languageId={this.props.languageId}
				deviceInfo={this.props.deviceInfo}
				userId={this.state.userId}
				email={this.state.email}
				handleLogout={this.handleLogout}
				setLanguageId={this.props.setLanguageId}
			/>
		);
	};
}

LoginController.propTypes = {
	languageId: PropTypes.string.isRequired,
	deviceInfo: PropTypes.object.isRequired,
	setLanguageId: PropTypes.func.isRequired,
	calculateFontSize: PropTypes.func.isRequired
};

export default LoginController;